import { FormInput, Modal, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { InternalError } from '@systemeio/util'
import React, { useState } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { fetchWithFieldAndCommonErrors } from 'shared/utils/fetch-with-errors'
import { BlogEntityDuplicateInterface, BlogEntityInterface } from '../types/blog-entity-interface'

interface Props {
  opened: boolean
  blogEntity: Pick<BlogEntityInterface, 'id' | 'name'>
  title?: string
  duplicateHandler: (id: BlogEntityInterface['id'], data: BlogEntityDuplicateInterface) => {}
  onClose: () => void
  onUpdateData: () => Promise<void>
}

const defaultErrors = {
  name: '',
  common: '',
}

const BlogEntityDuplicateModal = ({
  opened,
  blogEntity,
  title,
  onClose,
  onUpdateData,
  duplicateHandler,
}: Props) => {
  const { t } = useLocoTranslation()
  const initName = `${blogEntity.name} (copy)`
  const [name, setName] = useState(initName)
  const [isFetching, setIsFetching] = useState(false)
  const [errors, setErrors] = useState(defaultErrors)

  const onDuplicate = async (
    blogEntity: Props['blogEntity'],
    data: BlogEntityDuplicateInterface,
  ) => {
    try {
      setIsFetching(true)
      await fetchWithFieldAndCommonErrors(async () => {
        await duplicateHandler(blogEntity.id, data)
        await onUpdateData()
        toast.success(t('global.duplicated'))
        setIsFetching(false)
      }, setErrors)
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
      } else if (e instanceof InternalError) {
        setErrors(prev => ({ ...prev, common: t('core.error.internal_error_message') }))
      } else {
        toast.error(t('global.error'))
      }
    }
  }
  return (
    <Modal
      isFetching={isFetching}
      opened={opened}
      onClose={onClose}
      afterLeave={() => {
        setName(initName)
        setErrors(defaultErrors)
      }}
      title={title ?? t('global.duplicate')}
    >
      <form className="flex flex-col gap-6 lg:gap-10">
        <div className="flex flex-col justify-center gap-3">
          <FormInput
            value={name}
            label={t('global.duplicate_name')}
            onChange={e => setName(e.target.value)}
            error={errors.name}
          />
        </div>
        <div className="flex justify-center gap-4">
          <PrimaryButton
            isFetching={isFetching}
            disabled={name === ''}
            onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              await onDuplicate(blogEntity, { name })
            }}
          >
            {t('global.duplicate')}
          </PrimaryButton>
        </div>
        {errors.common && (
          <FieldErrorAndDescription error={errors.common} errorClassName={'text-center'} />
        )}
      </form>
    </Modal>
  )
}

export default BlogEntityDuplicateModal
